/**
 * ChannelPackageGrid Component V2
 *
 * Premium Grid-Layout für Channel Packages.
 * Zeigt alle Packages mit Status-Übersicht und Regenerate-Funktionen.
 *
 * Built with Tailwind CSS v4.1
 * WCAG 2.1 Level AA + BFSG konform
 */

import {
  Radio,
  AlertTriangle,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ChannelPackage } from '@/types/channel-packages';
import { ChannelPackageCard, ChannelPackageRow } from './ChannelPackageCard';
import { RegenerateAllButton } from './RegenerateButton';
import {
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  Spinner,
  EmptyState,
} from '@/components/v2/ui';
import { cn } from '@/lib/utils';

interface ReadinessData {
  readiness_score: number;
  image_rule_score: number;
  text_rule_score: number;
  issue_count: number;
}

interface ChannelPackageGridProps {
  packages: ChannelPackage[];
  contentUpdatedAt?: string;
  onPreview?: (pkg: ChannelPackage) => void;
  onEdit?: (pkg: ChannelPackage) => void;
  onRegenerate?: (pkg: ChannelPackage) => void;
  onRegenerateAll?: () => void;
  onViewVersions?: (pkg: ChannelPackage) => void;
  regeneratingIds?: Set<string>;
  isRegeneratingAll?: boolean;
  viewMode?: 'grid' | 'list';
  isLoading?: boolean;
  readinessMap?: Map<string, ReadinessData>;
}

export function ChannelPackageGrid({
  packages,
  contentUpdatedAt,
  onPreview,
  onEdit,
  onRegenerate,
  onRegenerateAll,
  onViewVersions,
  regeneratingIds = new Set(),
  isRegeneratingAll = false,
  viewMode = 'grid',
  isLoading = false,
  readinessMap,
}: ChannelPackageGridProps) {
  const { t } = useTranslation();
  // Count packages by status
  const outdatedCount = packages.filter((pkg) => {
    if (!contentUpdatedAt || !pkg.last_regenerated_at) return false;
    return new Date(contentUpdatedAt) > new Date(pkg.last_regenerated_at);
  }).length;

  const errorCount = packages.filter(
    (pkg) => pkg.regenerate_status === 'error'
  ).length;

  const processingCount = packages.filter(
    (pkg) =>
      pkg.regenerate_status === 'processing' ||
      pkg.regenerate_status === 'pending'
  ).length;

  // Loading State
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Spinner size="lg" className="mb-4" />
        <p className="text-neutral-500 dark:text-neutral-400 font-medium">
          {t('channelPackages.loading')}
        </p>
      </div>
    );
  }

  // Empty State
  if (packages.length === 0) {
    return (
      <EmptyState
        icon={<Radio className="h-12 w-12" />}
        title={t('channelPackages.noPackages')}
        description={t('products.noChannelPackagesYet')}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats & Actions */}
      <div
        className={cn(
          'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4',
          'p-4 rounded-xl',
          'bg-gradient-to-r from-neutral-50 to-neutral-100/50 dark:from-neutral-800 dark:to-neutral-900/50',
          'border border-neutral-200 dark:border-neutral-700'
        )}
      >
        {/* Status Badges */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Total Count */}
          <Badge variant="secondary" className="gap-1.5 px-3 py-1.5">
            <Radio className="h-3.5 w-3.5" />
            <span className="font-semibold">{packages.length}</span>
            <span className="text-neutral-500 dark:text-neutral-400">
              {t('channelPackages.channelCount_other', { count: packages.length })}
            </span>
          </Badge>

          {/* Outdated Warning */}
          {outdatedCount > 0 && (
            <Badge variant="warning" className="gap-1.5 px-3 py-1.5">
              <AlertTriangle className="h-3.5 w-3.5" />
              <span className="font-semibold">{outdatedCount}</span>
              <span>{t('channelPackages.outdated')}</span>
            </Badge>
          )}

          {/* Error Count */}
          {errorCount > 0 && (
            <Badge variant="error" className="gap-1.5 px-3 py-1.5">
              <XCircle className="h-3.5 w-3.5" />
              <span className="font-semibold">{errorCount}</span>
              <span>{t('channelPackages.errors')}</span>
            </Badge>
          )}

          {/* Processing Count */}
          {processingCount > 0 && (
            <Badge variant="info" className="gap-1.5 px-3 py-1.5">
              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              <span className="font-semibold">{processingCount}</span>
              <span>{t('channelPackages.inProgress')}</span>
            </Badge>
          )}
        </div>

        {/* Regenerate All Button */}
        {onRegenerateAll && packages.length > 1 && (
          <RegenerateAllButton
            onClick={onRegenerateAll}
            isRegenerating={isRegeneratingAll}
            count={packages.length}
            outdatedCount={outdatedCount}
          />
        )}
      </div>

      {/* Grid View */}
      {viewMode === 'grid' && (
        <div
          className={cn(
            'grid gap-6',
            'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
          )}
        >
          {packages.map((pkg) => (
            <ChannelPackageCard
              key={pkg.id}
              package_={pkg}
              contentUpdatedAt={contentUpdatedAt}
              onPreview={onPreview ? () => onPreview(pkg) : undefined}
              onEdit={onEdit ? () => onEdit(pkg) : undefined}
              onRegenerate={onRegenerate ? () => onRegenerate(pkg) : undefined}
              onViewVersions={
                onViewVersions ? () => onViewVersions(pkg) : undefined
              }
              isRegenerating={regeneratingIds.has(pkg.id) || isRegeneratingAll}
              readiness={readinessMap?.get(pkg.channel_id) || null}
            />
          ))}
        </div>
      )}

      {/* List/Table View */}
      {viewMode === 'list' && (
        <div
          className={cn(
            'rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden',
            'bg-white dark:bg-neutral-900 shadow-sm'
          )}
        >
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50/80 dark:bg-neutral-800/80">
                <TableHead className="font-semibold text-neutral-700 dark:text-neutral-300">
                  {t('channelPackages.tableHeaderChannel')}
                </TableHead>
                <TableHead className="font-semibold text-neutral-700 dark:text-neutral-300">
                  {t('channelPackages.tableHeaderType')}
                </TableHead>
                <TableHead className="font-semibold text-neutral-700 dark:text-neutral-300">
                  {t('channelPackages.tableHeaderStatus')}
                </TableHead>
                <TableHead className="font-semibold text-neutral-700 dark:text-neutral-300">
                  {t('channelPackages.tableHeaderLastRegen')}
                </TableHead>
                <TableHead className="text-right font-semibold text-neutral-700 dark:text-neutral-300">
                  {t('channelPackages.tableHeaderActions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {packages.map((pkg) => (
                <ChannelPackageRow
                  key={pkg.id}
                  package_={pkg}
                  contentUpdatedAt={contentUpdatedAt}
                  onPreview={onPreview ? () => onPreview(pkg) : undefined}
                  onEdit={onEdit ? () => onEdit(pkg) : undefined}
                  onRegenerate={
                    onRegenerate ? () => onRegenerate(pkg) : undefined
                  }
                  onViewVersions={
                    onViewVersions ? () => onViewVersions(pkg) : undefined
                  }
                  isRegenerating={
                    regeneratingIds.has(pkg.id) || isRegeneratingAll
                  }
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

